import {useState} from 'react'
import {View} from 'react-native'
import {type MessageDescriptor} from '@lingui/core'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {gradeLabel} from '#/lib/curriculum'
import {type NavigationProp} from '#/lib/routes/types'
import {
  type Classroom,
  classroomLevelName,
  countryName,
  encodeClassroomInvite,
  learnerMayJoinClassroom,
  makeClassroomInvite,
  readClassroomInviteFromText,
  useClassroomActor,
} from '#/state/classrooms'
import {
  ClassroomApiError,
  classroomErrorMessage,
  classroomQuery,
  type ClassroomViewJson,
  toRoom,
  useJoinClassroomMutation,
} from '#/state/queries/classrooms'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Lock_Stroke2_Corner0_Rounded as LockIcon} from '#/components/icons/Lock'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {LearnerLevelLink} from './LearnerLevelDialog'

/** The server's limit on the message sent with an ask. */
export const JOIN_MESSAGE_MAX_GRAPHEMES = 200

/** Characters as a reader counts them (an emoji is one), where the runtime can tell. */
export function countGraphemes(text: string): number {
  const Segmenter = (Intl as {Segmenter?: typeof Intl.Segmenter}).Segmenter
  if (!Segmenter) return text.length
  let count = 0
  for (const _segment of new Segmenter(undefined, {
    granularity: 'grapheme',
  }).segment(text)) {
    count++
  }
  return count
}

type JoinOutcome = {kind: 'joined'} | {kind: 'error'; code: string}

function errorCode(error: unknown): string {
  return error instanceof ClassroomApiError ? error.code : ''
}

/**
 * Sends a join for one room and turns the server's answer into what the
 * dialog does: an ask closes the dialog with a receipt, a seat stays on screen
 * with the way into the room, and a refusal is shown in the dialog.
 */
function useJoin({
  classroom,
  control,
}: {
  classroom: Classroom | undefined
  control: DialogControlProps
}) {
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const mutation = useJoinClassroomMutation(classroom?.id ?? '')
  const [outcome, setOutcome] = useState<JoinOutcome | null>(null)

  const openRoom = () => {
    if (!classroom) return
    control.close(() => {
      navigation.navigate('Classroom', {
        name: classroom.ownerHandle,
        rkey: classroom.handle,
      })
    })
  }

  const join = (vars: {code?: string; message?: string}) => {
    if (!classroom || mutation.isPending) return
    setOutcome(null)
    mutation.mutate(vars, {
      onSuccess: ({status}) => {
        if (status === 'requested') {
          control.close(() => {
            Toast.show(l`Request sent. Whoever runs the classroom decides.`)
          })
        } else if (status === 'joined') {
          setOutcome({kind: 'joined'})
        } else {
          openRoom()
        }
      },
      onError: error => setOutcome({kind: 'error', code: errorCode(error)}),
    })
  }

  return {
    join,
    openRoom,
    outcome,
    setOutcome,
    isPending: mutation.isPending,
  }
}

/**
 * The code door: a code the school read out, or an invitation link pasted in.
 *
 * Opened from a room (`classroom` given), it joins that room with the code. Opened
 * from anywhere else, it looks the code up on the server and opens the room it
 * names, carrying the code along, so the join happens on the room's own page
 * where the visitor can see what they are joining.
 */
export function JoinByCodeDialog({
  control,
  classroom,
  code: initialCode,
}: {
  control: DialogControlProps
  /** The room this dialog was opened from, when it was opened from one. */
  classroom?: Classroom
  /** A code already in hand, e.g. the one the invitation link carried. */
  code?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const agent = useAgent()
  const actor = useClassroomActor()
  const navigation = useNavigation<NavigationProp>()
  const [code, setCode] = useState(initialCode ?? '')
  const [resolving, setResolving] = useState(false)
  const {join, openRoom, outcome, setOutcome, isPending} = useJoin({
    classroom,
    control,
  })

  const openCard = (card: ReturnType<typeof makeClassroomInvite>) => {
    control.close(() => {
      navigation.navigate('Classroom', {
        name: card.ownerHandle,
        rkey: card.handle,
        invite: encodeClassroomInvite(card),
      })
    })
  }

  const onSubmit = async () => {
    const text = code.trim()
    if (!actor || !text || isPending || resolving) return
    const card = readClassroomInviteFromText(text)
    if (classroom && (!card || card.id === classroom.id)) {
      join({code: card ? card.code : text})
      return
    }
    if (card) {
      openCard(card)
      return
    }
    setOutcome(null)
    setResolving(true)
    try {
      const res = await classroomQuery<{classroom: ClassroomViewJson}>(
        agent,
        'app.taderok.classroom.resolveInvite',
        {code: text},
      )
      openCard({
        ...makeClassroomInvite(toRoom(res.classroom).classroom),
        code: text,
      })
    } catch (e) {
      setOutcome({kind: 'error', code: errorCode(e)})
    } finally {
      setResolving(false)
    }
  }

  const onClose = () => {
    setCode(initialCode ?? '')
    setOutcome(null)
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Join a classroom with a code`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Join a classroom`}</Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {initialCode
              ? l`Your invitation carries the code. Join with it, and whoever runs the classroom lets you in or receives your request.`
              : l`Enter the code you were given, or paste the invitation link.`}
          </Text>

          <View style={[a.gap_sm]}>
            <TextField.LabelText>{l`Code`}</TextField.LabelText>
            <TextField.Root>
              <TextField.Icon icon={LockIcon} />
              <TextField.Input
                testID="classroomJoinCodeInput"
                label={l`Code`}
                placeholder="ABC123"
                defaultValue={initialCode}
                autoCapitalize="none"
                autoCorrect={false}
                onChangeText={setCode}
                onSubmitEditing={() => void onSubmit()}
                returnKeyType="go"
              />
            </TextField.Root>
          </View>

          {outcome ? (
            <JoinOutcomeMessage
              outcome={outcome}
              testID="classroomJoinError"
              classroomId={classroom?.id}
              onLevelSaved={() => setOutcome(null)}
              onOpen={openRoom}
            />
          ) : null}

          {outcome?.kind === 'joined' ? null : (
            <Button
              testID="classroomJoinSubmit"
              label={classroom ? l`Join with this code` : l`Find the classroom`}
              size="large"
              color="primary"
              disabled={
                !actor || code.trim().length === 0 || isPending || resolving
              }
              onPress={() => void onSubmit()}>
              <ButtonText>
                {classroom ? l`Join with this code` : l`Find the classroom`}
              </ButtonText>
            </Button>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * Ask, instead of joining with a code: the way into a public classroom. A code
 * the invitation carried rides along, so a room that seats code holders at
 * once does so.
 */
export function AskToJoinDialog({
  control,
  classroom,
  code,
}: {
  control: DialogControlProps
  classroom: Classroom
  code?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const [message, setMessage] = useState('')
  const {join, openRoom, outcome, setOutcome, isPending} = useJoin({
    classroom,
    control,
  })

  /*
   * An instant hint from the same rule the server applies, so a learner whose
   * level or country does not match is told before anything is sent. The
   * server still decides.
   */
  const gate = actor
    ? learnerMayJoinClassroom(classroom, actor)
    : {allowed: true, reason: null}

  const length = countGraphemes(message)
  const tooLong = length > JOIN_MESSAGE_MAX_GRAPHEMES

  const onSubmit = () => {
    if (!actor || !gate.allowed || tooLong) return
    join({code, message: message.trim() || undefined})
  }

  const onClose = () => {
    setMessage('')
    setOutcome(null)
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Ask to join ${classroom.name}`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>
            {l`Ask to join ${classroom.name}`}
          </Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {l`Whoever runs the classroom will see your request and can accept it.`}
          </Text>

          {!gate.allowed && gate.reason === 'country' ? (
            <Text
              accessibilityRole="alert"
              testID="classroomAskCountryBlocked"
              style={[a.text_md, {color: t.palette.negative_500}]}>
              {l`This classroom is for learners in ${countryName(classroom.country)}. Your account is in ${countryName(actor?.country)}, and a classroom takes the learners of its own country — an international classroom takes every country.`}
            </Text>
          ) : !gate.allowed ? (
            <Text
              accessibilityRole="alert"
              testID="classroomAskLevelBlocked"
              style={[a.text_md, {color: t.palette.negative_500}]}>
              {gate.reason === 'grade'
                ? l`This classroom teaches ${classroomLevelText(classroom)}. Your year is different, so you cannot join it — `
                : gate.reason === 'level'
                  ? l`This classroom is for ${classroomLevelText(classroom)}. Your level is different, so you cannot join it — `
                  : l`This classroom is for ${classroomLevelText(classroom)} and checks your level. Tell us your level to join it — `}
              <LearnerLevelLink
                testID="classroomAskLevelLink"
                onSaved={() => setOutcome(null)}
                classroomId={classroom.id}
              />
              {'.'}
            </Text>
          ) : null}

          <View style={[a.gap_sm]}>
            <TextField.LabelText>{l`Message (optional)`}</TextField.LabelText>
            <TextField.Root isInvalid={tooLong}>
              <TextField.Input
                testID="classroomAskMessageInput"
                label={l`Message (optional)`}
                placeholder={l`Say who you are`}
                multiline
                numberOfLines={3}
                onChangeText={setMessage}
              />
            </TextField.Root>
            <Text
              testID="classroomAskMessageCount"
              style={[
                a.text_sm,
                a.text_right,
                tooLong
                  ? {color: t.palette.negative_500}
                  : t.atoms.text_contrast_medium,
              ]}>
              {`${length}/${JOIN_MESSAGE_MAX_GRAPHEMES}`}
            </Text>
          </View>

          {outcome ? (
            <JoinOutcomeMessage
              outcome={outcome}
              testID="classroomAskError"
              classroomId={classroom.id}
              onLevelSaved={() => setOutcome(null)}
              onOpen={openRoom}
            />
          ) : null}

          {outcome?.kind === 'joined' ? null : (
            <Button
              testID="classroomAskSubmit"
              label={l`Send request`}
              size="large"
              color="primary"
              disabled={!actor || !gate.allowed || tooLong || isPending}
              onPress={onSubmit}>
              <ButtonText>{l`Send request`}</ButtonText>
            </Button>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * The level a room teaches, as the line a person reads: "Primary School · 1st
 * year", or just the level when it has no year of its own.
 */
function classroomLevelText(classroom: Classroom): string {
  const grade = classroom.grade
    ? (gradeLabel(classroom.grade)?.message ?? classroom.grade)
    : null
  return [classroomLevelName(classroom), grade].filter(Boolean).join(' · ')
}

/** Refusals the learner can fix by telling us their level. */
const LEVEL_CODES = new Set(['LevelUnknown', 'LevelMismatch'])

/**
 * What the server answered, in the dialog: a seat with the way into the room,
 * or a refusal in plain words (with the level picker when the level is why).
 */
function JoinOutcomeMessage({
  outcome,
  testID,
  classroomId,
  onLevelSaved,
  onOpen,
}: {
  outcome: JoinOutcome
  testID: string
  classroomId?: string
  onLevelSaved: () => void
  onOpen: () => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()

  if (outcome.kind === 'joined') {
    return (
      <View style={[a.gap_md]}>
        <Text accessibilityRole="alert" style={[a.text_md, t.atoms.text]}>
          {l`You are in.`}
        </Text>
        <Button
          testID="classroomJoinOpen"
          label={l`Open the classroom`}
          size="large"
          color="primary"
          onPress={onOpen}>
          <ButtonText>{l`Open the classroom`}</ButtonText>
        </Button>
      </View>
    )
  }

  return (
    <View style={[a.gap_xs]}>
      <Text
        accessibilityRole="alert"
        testID={testID}
        style={[a.text_md, {color: t.palette.negative_500}]}>
        {classroomErrorMessage(
          // Only ever handed the error module's own `msg` descriptors
          d => i18n._(d as MessageDescriptor),
          outcome.code,
        )}
      </Text>
      {LEVEL_CODES.has(outcome.code) && actor?.accountType === 'learner' ? (
        <Text style={[a.text_md]}>
          <Trans>
            You can{' '}
            <LearnerLevelLink
              testID="classroomJoinLevelLink"
              onSaved={onLevelSaved}
              classroomId={classroomId}
            />
            .
          </Trans>
        </Text>
      ) : null}
    </View>
  )
}
